/**
 * In-flight granular mutation registry, keyed by trip.
 *
 * Granular routes (pins, legs, leg membership) each bump trips.revision on
 * the server. If the debounced full-trip save fires while one is in flight,
 * the PUT carries a revision that is already stale, gets a 409, and the
 * conflict path reloads server state — silently discarding the user's
 * unsaved schedule edits. Every granular request must therefore register
 * here, and doSave waits for the trip's registry to drain before it reads
 * the revision it will send.
 */

const pendingByTrip = new Map<string, Set<Promise<unknown>>>();

/** Register a granular request; returns the same promise for chaining. */
export function trackGranularRequest<T>(
  tripId: string,
  request: Promise<T>,
): Promise<T> {
  let inFlight = pendingByTrip.get(tripId);
  if (!inFlight) {
    inFlight = new Set();
    pendingByTrip.set(tripId, inFlight);
  }
  inFlight.add(request);
  const remove = () => {
    inFlight.delete(request);
    if (inFlight.size === 0) pendingByTrip.delete(tripId);
  };
  request.then(remove, remove);
  return request;
}

// A drag gesture fires granular requests back to back, so new ones can start
// while earlier ones are awaited. Bounded so a pathological stream cannot
// stall saving forever — on overflow the save proceeds and at worst hits the
// 409 path it would have hit without the barrier.
const MAX_WAIT_ROUNDS = 20;

/** Resolve once every registered request for the trip has settled. */
export async function waitForGranularRequests(tripId: string): Promise<void> {
  for (let round = 0; round < MAX_WAIT_ROUNDS; round++) {
    const inFlight = pendingByTrip.get(tripId);
    if (!inFlight || inFlight.size === 0) return;
    await Promise.allSettled([...inFlight]);
  }
}

/** Drop all tracking for a trip (on unload). Requests still settle normally. */
export function clearGranularRequests(tripId: string): void {
  pendingByTrip.delete(tripId);
}
